import { FC, useMemo } from 'react';

import { DEFAULT_AUCTION_AUTO_STRATEGY_SETTINGS } from 'utils/constants/auction';
import { Settings } from 'utils/constants/dynamicSettings';

import { GridColumn, GridLayout, GridRow, VSpacingContainer } from '@hh.ru/magritte-ui';

import { useBiddingStrategyTags } from 'hooks/auction/useBiddingStrategyTags';
import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';

import { AutoStrategyDetailsModal } from 'components/AuctionBuilder/BiddingStrategySelect/AutoStrategyDetailsModal/AutoStrategyDetailsModal';
import { AutoStrategyLabelTooltipContent } from 'components/AuctionBuilder/BiddingStrategySelect/AutoStrategyLabelTooltipContent/AutoStrategyLabelTooltipContent';
import { BiddingStrategyCard } from 'components/AuctionBuilder/BiddingStrategySelect/BiddingStrategyCard/BiddingStrategyCard';
import { AuctionAutoStrategySettings } from 'components/AuctionBuilder/types/auctionAutoStrategySettings';

import { BiddingStrategy } from 'store/models/auctionData';
import { setMultiCreationBiddingStrategy } from 'store/models/multiCreation';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    label: 'AuctionBuilder.BiddingStrategySelect.label',
    [BiddingStrategy.Auto]: {
        label: 'AuctionBuilder.BiddingStrategySelect.BiddingStrategyCard.auto.label',
        description: {
            p1: 'MultiCreationAuction.BiddingStrategySelect.BiddingStrategyCard.auto.description.p1',
            p2: 'MultiCreationAuction.BiddingStrategySelect.BiddingStrategyCard.auto.description.p2',
        },
        labelTooltipContent: 'AuctionBuilder.BiddingStrategySelect.BiddingStrategyCard.auto.labelTooltipContent',
    },
    [BiddingStrategy.Manual]: {
        label: 'AuctionBuilder.BiddingStrategySelect.BiddingStrategyCard.manual.label',
        description: 'AuctionBuilder.BiddingStrategySelect.BiddingStrategyCard.manual.description',
        labelTooltipContent: null,
    },
};

export const BiddingStrategySelect: FC = () => {
    const biddingStrategy = useSelector(({ multiCreation }) => multiCreation.biddingStrategy);

    const trls = useTrls();
    const dispatch = useDispatch();
    const settings = useSettings();
    const [isDetailsVisible, setIsDetailsVisibleOn, setIsDetailsVisibleOff] = useOnOffState(false);
    const biddingStrategyTags = useBiddingStrategyTags();
    const autoStrategyBenefits = settings(Settings.AuctionAutoStrategySettings) as AuctionAutoStrategySettings | null;
    const { applicationsGrowthPercent, applicationsCostReductionPercent } = autoStrategyBenefits || {};

    const handleBiddingStrategyChange = (newBiddingStrategy: BiddingStrategy) => {
        if (newBiddingStrategy === biddingStrategy) {
            return;
        }

        dispatch(setMultiCreationBiddingStrategy(newBiddingStrategy));
    };

    const strategyDescriptions = useMemo<Record<BiddingStrategy, string[]>>(
        () => ({
            [BiddingStrategy.Auto]: [
                trls(TrlKeys[BiddingStrategy.Auto].description.p1),
                trls(TrlKeys[BiddingStrategy.Auto].description.p2),
            ],
            [BiddingStrategy.Manual]: [trls(TrlKeys[BiddingStrategy.Manual].description)],
        }),
        [trls]
    );

    return (
        <GridLayout>
            <GridRow>
                <GridColumn xs={4} s={8} m={8}>
                    <VSpacingContainer default={12}>
                        {Object.values(BiddingStrategy).map((key) => (
                            <BiddingStrategyCard
                                key={key}
                                checked={key === biddingStrategy}
                                label={trls(TrlKeys[key as BiddingStrategy].label)}
                                description={strategyDescriptions[key as BiddingStrategy]}
                                tags={biddingStrategyTags[key as BiddingStrategy]}
                                placement="multicreation"
                                labelTooltipContent={
                                    key === BiddingStrategy.Auto ? (
                                        <AutoStrategyLabelTooltipContent onDetailsButtonClick={setIsDetailsVisibleOn} />
                                    ) : (
                                        trls(TrlKeys[key as BiddingStrategy].labelTooltipContent) || undefined
                                    )
                                }
                                onClick={() => handleBiddingStrategyChange(key as BiddingStrategy)}
                                dataQa={`bidding-strategy-${key}`}
                            />
                        ))}
                    </VSpacingContainer>
                </GridColumn>
            </GridRow>
            <AutoStrategyDetailsModal
                isVisible={isDetailsVisible}
                onClose={setIsDetailsVisibleOff}
                applicationsGrowthPercent={
                    applicationsGrowthPercent ?? DEFAULT_AUCTION_AUTO_STRATEGY_SETTINGS.applicationsGrowthPercent
                }
                applicationsCostReductionPercent={
                    applicationsCostReductionPercent ??
                    DEFAULT_AUCTION_AUTO_STRATEGY_SETTINGS.applicationsCostReductionPercent
                }
            />
        </GridLayout>
    );
};
